import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import {
  BookOpen,
  Search,
  Plus,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  RefreshCw,
  Download,
  Upload,
  Tag,
  Folder,
  FileText,
  Globe,
  Building2,
  FlaskConical,
  Leaf,
  Cpu,
  Heart,
  Scale,
  Landmark,
  Sparkles,
  CheckCircle2,
  Clock,
  Link2,
  Copy,
  Info
} from "lucide-react";

// Mock ANZSRC FOR codes (Fields of Research)
const forCodes = [
  {
    code: "30",
    name: "Agricultural, Veterinary and Food Sciences",
    icon: Leaf,
    children: [
      { code: "3001", name: "Agricultural Biotechnology", count: 45 },
      { code: "3002", name: "Agriculture, Land and Farm Management", count: 78 },
      { code: "3003", name: "Animal Production", count: 34 },
      { code: "3004", name: "Crop and Pasture Production", count: 56 },
      { code: "3005", name: "Fisheries Sciences", count: 23 },
      { code: "3006", name: "Food Sciences", count: 89 },
      { code: "3007", name: "Forestry Sciences", count: 41 },
      { code: "3008", name: "Horticultural Production", count: 29 },
      { code: "3009", name: "Veterinary Sciences", count: 67 }
    ]
  },
  {
    code: "31",
    name: "Biological Sciences",
    icon: FlaskConical,
    children: [
      { code: "3101", name: "Biochemistry and Cell Biology", count: 156 },
      { code: "3102", name: "Bioinformatics and Computational Biology", count: 89 },
      { code: "3103", name: "Ecology", count: 234 },
      { code: "3104", name: "Evolutionary Biology", count: 67 },
      { code: "3105", name: "Genetics", count: 123 },
      { code: "3106", name: "Industrial Biotechnology", count: 45 },
      { code: "3107", name: "Microbiology", count: 98 },
      { code: "3108", name: "Plant Biology", count: 76 },
      { code: "3109", name: "Zoology", count: 112 }
    ]
  },
  {
    code: "37",
    name: "Earth Sciences",
    icon: Globe,
    children: [
      { code: "3701", name: "Atmospheric Sciences", count: 87 },
      { code: "3702", name: "Climate Change Science", count: 156 },
      { code: "3703", name: "Geochemistry", count: 45 },
      { code: "3704", name: "Geoinformatics", count: 34 },
      { code: "3705", name: "Geology", count: 78 },
      { code: "3706", name: "Geophysics", count: 56 },
      { code: "3707", name: "Hydrology", count: 89 },
      { code: "3708", name: "Oceanography", count: 123 },
      { code: "3709", name: "Physical Geography and Environmental Geoscience", count: 67 }
    ]
  },
  {
    code: "40",
    name: "Engineering",
    icon: Cpu,
    children: [
      { code: "4001", name: "Aerospace Engineering", count: 34 },
      { code: "4002", name: "Automotive Engineering", count: 23 },
      { code: "4003", name: "Biomedical Engineering", count: 78 },
      { code: "4004", name: "Chemical Engineering", count: 56 },
      { code: "4005", name: "Civil Engineering", count: 145 },
      { code: "4006", name: "Communications Engineering", count: 67 },
      { code: "4007", name: "Control Engineering, Mechatronics and Robotics", count: 89 },
      { code: "4008", name: "Electrical Engineering", count: 112 },
      { code: "4009", name: "Electronics, Sensors and Digital Hardware", count: 98 }
    ]
  },
  {
    code: "42",
    name: "Health Sciences",
    icon: Heart,
    children: [
      { code: "4201", name: "Allied Health and Rehabilitation Science", count: 67 },
      { code: "4202", name: "Epidemiology", count: 189 },
      { code: "4203", name: "Health Services and Systems", count: 134 },
      { code: "4204", name: "Midwifery", count: 23 },
      { code: "4205", name: "Nursing", count: 78 },
      { code: "4206", name: "Public Health", count: 245 },
      { code: "4207", name: "Sports Science and Exercise", count: 56 }
    ]
  }
];

// Mock RVA vocabularies
const rvaVocabularies = [
  {
    id: "anzsrc-for",
    name: "ANZSRC Fields of Research",
    publisher: "Australian Bureau of Statistics",
    version: "2020",
    status: "current",
    termsCount: 1238,
    lastUpdated: "2023-06-15"
  },
  {
    id: "anzsrc-seo",
    name: "ANZSRC Socio-Economic Objectives",
    publisher: "Australian Bureau of Statistics",
    version: "2020",
    status: "current",
    termsCount: 867,
    lastUpdated: "2023-06-15"
  },
  {
    id: "gcmd-science",
    name: "GCMD Science Keywords",
    publisher: "NASA",
    version: "17.1",
    status: "current",
    termsCount: 2456,
    lastUpdated: "2024-01-10"
  },
  {
    id: "iso-19115",
    name: "ISO 19115 Topic Categories",
    publisher: "ISO",
    version: "2014",
    status: "current",
    termsCount: 19,
    lastUpdated: "2022-08-20"
  },
  {
    id: "rifcs-type",
    name: "RIF-CS Record Types",
    publisher: "ARDC",
    version: "1.6",
    status: "current",
    termsCount: 45,
    lastUpdated: "2023-09-01"
  },
  {
    id: "access-rights",
    name: "Access Rights Vocabulary",
    publisher: "ARDC",
    version: "1.0",
    status: "current",
    termsCount: 8,
    lastUpdated: "2023-03-12"
  }
];

// Mock recently used terms
const recentlyUsed = [
  { code: "3702", name: "Climate Change Science", vocabulary: "ANZSRC FOR", count: 89 },
  { code: "3103", name: "Ecology", vocabulary: "ANZSRC FOR", count: 67 },
  { code: "4206", name: "Public Health", vocabulary: "ANZSRC FOR", count: 45 },
  { code: "3102", name: "Bioinformatics and Computational Biology", vocabulary: "ANZSRC FOR", count: 34 },
  { code: "3708", name: "Oceanography", vocabulary: "ANZSRC FOR", count: 28 }
];

// Mock AI suggestions
const aiSuggestions = [
  {
    term: "Climate Modeling",
    suggestedCode: "3702",
    suggestedName: "Climate Change Science",
    confidence: 0.94,
    reason: "Based on dataset keywords and abstract analysis"
  },
  {
    term: "Marine Biology",
    suggestedCode: "3109",
    suggestedName: "Zoology",
    confidence: 0.78,
    reason: "Consider also 3708 Oceanography based on spatial coverage"
  },
  {
    term: "Machine Learning",
    suggestedCode: "4602",
    suggestedName: "Artificial Intelligence",
    confidence: 0.91,
    reason: "Based on methodology described in data documentation"
  }
];

export default function ResearchVocabulariesPage() {
  const [activeTab, setActiveTab] = useState("browse");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCodes, setExpandedCodes] = useState<string[]>(["31"]);
  const [selectedVocabulary, setSelectedVocabulary] = useState("anzsrc-for");

  const toggleExpanded = (code: string) => {
    setExpandedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const getIcon = (code: string) => {
    const category = forCodes.find((c) => c.code === code);
    if (category) {
      const IconComponent = category.icon;
      return <IconComponent className="h-5 w-5" />;
    }
    return <Folder className="h-5 w-5" />;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <BookOpen className="h-8 w-8 text-primary" />
            Research Vocabularies
          </h1>
          <p className="text-muted-foreground mt-1">
            ANZSRC codes, RVA vocabularies, and controlled terminology management
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync RVA
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Vocabulary
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add External Vocabulary</DialogTitle>
                <DialogDescription>
                  Import a vocabulary from Research Vocabularies Australia or provide a custom URL
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Vocabulary Source</label>
                  <Select defaultValue="rva">
                    <SelectTrigger>
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rva">Research Vocabularies Australia</SelectItem>
                      <SelectItem value="custom">Custom URL</SelectItem>
                      <SelectItem value="file">Upload File</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Vocabulary ID or URL</label>
                  <Input placeholder="e.g., anzsrc-for-2020 or https://..." />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline">Cancel</Button>
                <Button>Import Vocabulary</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                <BookOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">6</p>
                <p className="text-sm text-muted-foreground">Vocabularies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                <Tag className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">4,633</p>
                <p className="text-sm text-muted-foreground">Total Terms</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                <Link2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">12,456</p>
                <p className="text-sm text-muted-foreground">Term Usages</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900">
                <Sparkles className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">89%</p>
                <p className="text-sm text-muted-foreground">AI Match Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="browse" className="gap-2">
            <Folder className="h-4 w-4" />
            Browse ANZSRC
          </TabsTrigger>
          <TabsTrigger value="vocabularies" className="gap-2">
            <BookOpen className="h-4 w-4" />
            All Vocabularies
          </TabsTrigger>
          <TabsTrigger value="ai" className="gap-2">
            <Sparkles className="h-4 w-4" />
            AI Suggestions
          </TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* ANZSRC Browser */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>ANZSRC Fields of Research (2020)</CardTitle>
                <CardDescription>
                  Browse and select classification codes for your research data
                </CardDescription>
                <div className="pt-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search FOR codes..."
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {forCodes.map((category) => (
                    <Collapsible
                      key={category.code}
                      open={expandedCodes.includes(category.code)}
                      onOpenChange={() => toggleExpanded(category.code)}
                    >
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
                          {expandedCodes.includes(category.code) ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                          <div className="p-2 rounded bg-primary/10">
                            {getIcon(category.code)}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">
                              <span className="text-primary font-mono mr-2">
                                {category.code}
                              </span>
                              {category.name}
                            </p>
                          </div>
                          <Badge variant="secondary">
                            {category.children.length} codes
                          </Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="ml-10 mt-2 space-y-1">
                          {category.children.map((child) => (
                            <div
                              key={child.code}
                              className="flex items-center gap-3 p-2 pl-4 border-l-2 border-muted hover:border-primary hover:bg-muted/30 cursor-pointer transition-colors rounded-r"
                            >
                              <span className="font-mono text-sm text-primary">
                                {child.code}
                              </span>
                              <span className="flex-1 text-sm">{child.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {child.count} records
                              </Badge>
                              <Button variant="ghost" size="sm" className="h-7">
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recently Used & Quick Actions */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    Recently Used
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {recentlyUsed.map((term, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 border rounded hover:bg-muted/50 cursor-pointer transition-colors"
                    >
                      <span className="font-mono text-sm text-primary">
                        {term.code}
                      </span>
                      <span className="flex-1 text-sm truncate">{term.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {term.count}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Quick Links</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <ExternalLink className="h-4 w-4" />
                    Research Vocabularies Australia
                  </Button>
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <ExternalLink className="h-4 w-4" />
                    ANZSRC 2020 Documentation
                  </Button>
                  <Button variant="outline" className="w-full justify-start gap-2">
                    <Download className="h-4 w-4" />
                    Export Usage Report
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="vocabularies" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Registered Vocabularies</CardTitle>
              <CardDescription>
                Controlled vocabularies available for metadata annotation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {rvaVocabularies.map((vocab) => (
                  <div
                    key={vocab.id}
                    className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="p-3 rounded-lg bg-primary/10">
                      <BookOpen className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{vocab.name}</p>
                        <Badge variant="outline">v{vocab.version}</Badge>
                        {vocab.status === "current" && (
                          <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Current
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Publisher: {vocab.publisher} • Updated: {vocab.lastUpdated}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{vocab.termsCount.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">terms</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View
                      </Button>
                      <Button variant="outline" size="sm">
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-yellow-500" />
                  AI Term Suggestions
                </CardTitle>
                <CardDescription>
                  Intelligent vocabulary matching based on your data content
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {aiSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="p-4 border rounded-lg space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{suggestion.term}</span>
                      </div>
                      <Badge
                        variant={
                          suggestion.confidence >= 0.9
                            ? "default"
                            : suggestion.confidence >= 0.8
                            ? "secondary"
                            : "outline"
                        }
                      >
                        {(suggestion.confidence * 100).toFixed(0)}% match
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                      <ChevronRight className="h-4 w-4 text-primary" />
                      <span className="font-mono text-sm text-primary">
                        {suggestion.suggestedCode}
                      </span>
                      <span className="text-sm">{suggestion.suggestedName}</span>
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      {suggestion.reason}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button size="sm" className="flex-1">
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Accept
                      </Button>
                      <Button variant="outline" size="sm">
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm">
                        Dismiss
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Batch Classification</CardTitle>
                <CardDescription>
                  Apply AI-suggested codes to multiple records at once
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 border rounded-lg bg-muted/30">
                  <div className="flex items-center gap-3 mb-3">
                    <Upload className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">Upload records for classification</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Upload a CSV or JSON file containing record titles and descriptions
                    to receive AI-powered ANZSRC code suggestions.
                  </p>
                  <Button variant="outline" className="w-full">
                    <Upload className="h-4 w-4 mr-2" />
                    Select File
                  </Button>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <Sparkles className="h-5 w-5 text-yellow-500" />
                    <span className="font-medium">How AI Classification Works</span>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                      Analyzes titles, descriptions, and keywords
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                      Matches against ANZSRC 2020 definitions
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                      Suggests primary and secondary codes
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                      Provides confidence scores for review
                    </li>
                  </ul>
                </div>

                <div className="flex items-center gap-2 p-3 border rounded-lg bg-blue-50 dark:bg-blue-950">
                  <Info className="h-5 w-5 text-blue-500" />
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    AI suggestions are reviewed by domain experts to ensure accuracy
                    before being applied to your research records.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
